// Snake & Ladder — playable React page.
//
// UI ported from the Ludo client so the lobby + gameplay look and feel
// identical (just green-themed, with the 10×10 board). The shared visual
// language lives in ./snake_ladder.css — a scoped copy of ludo.css under
// the `.sl-root` wrapper so it can't collide with Ludo in the bundle.
//
// Interaction model matches Ludo: each seat has a dice in its panel that
// the human taps to roll; after a roll the eligible tokens pulse ON THE
// BOARD and are tapped to move (no separate A/B buttons).
//
// Server is authoritative — we never simulate rules locally; every move
// waits for the server's `token_moved` frame carrying the final state.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useWallet, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader, flattenImageList } from '@baaligames/shared-ui/lib/useAssetPreloader';
import {
  SnakeLadderWS,
  joinQueueMsg, leaveGameMsg, rollDiceMsg, pickTokenMsg,
  type WSStatus,
} from './ws';
import SnakeLadderBoard, {
  type SnakeData, type LadderData, type TokenRender, EGG_RUN_SCALE,
} from './Board';
import { centerOf, snakePathPoints, snakeKind } from './board';
import { WSVoice } from './wsVoice';
import {
  primeAudio, installAudioUnlock, setSoundEnabled as setSfxEnabled,
  playDiceRollSound, playJumpSound, playChickenCluck, playKickSound,
  playWinFanfare, playLossTone, playTap, playYourTurnChime,
  playSnakeHiss, playSafeZoneSound,
  startUrgencyBeep, stopUrgencyBeep,
} from './sounds';

// Relaxed animation timings (ms). Square hops read clearly; snake/ladder
// slides are slower so the player can follow the token down/up.
const HOP_MS     = 230;  // per square along the numbered path
const HOP_HEIGHT = 44;   // arc height of each jump (viewBox units)
const SLIDE_MS   = 900;  // snake slide-down / ladder climb-up
const DICE_HOLD  = 450;  // pause after the dice settles, before moving
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
import './snake_ladder.css';

// ─── Types matching the server protocol ──────────────────────────
type Color = 'red' | 'green' | 'yellow' | 'blue';
type EngineToken = { letter: 'A' | 'B'; position: number; isHomed: boolean };
type EnginePlayer = {
  playerId:     number;
  userId:       string;
  color:        Color;
  displayName:  string;
  avatarUrl?:   string | null;
  isBot:        boolean;
  tokens:       EngineToken[];
  strikes:      number;
  connected:    boolean;
  seatStatus:   'active' | 'forfeited' | 'finished';
  finishedRank: number | null;
};
type EngineState = {
  status:             'pending' | 'playing' | 'finished';
  players:            EnginePlayer[];
  currentPlayerIndex: number;
  turnId:             number;
  awaiting:           'roll' | 'token_pick' | 'finished';
  lastRoll:           { value: number; byPlayerId: number } | null;
  winnerUserId:       string | null;
};

type Phase = 'lobby' | 'queue' | 'vs' | 'playing' | 'result';

const COLOR_HEX: Record<Color, string> = {
  red: '#ef4444', green: '#22c55e', yellow: '#facc15', blue: '#3b82f6',
};

// Assets live under /public/games/snake_ladder/ (same filenames as Ludo).
const IMG = {
  settings: '/games/snake_ladder/Settings.png',
  coin:     '/games/snake_ladder/Coin.png',
  plus:     '/games/snake_ladder/Plus.png',
  logo:     '/games/snake_ladder/Logo.png',
  pawns2:   '/games/snake_ladder/Pawn-2.png',
  pawns4:   '/games/snake_ladder/Pawn-4.png',
  cash1:    '/games/snake_ladder/Cash-1.png',
  cash2:    '/games/snake_ladder/Cash-2.png',
  cash3:    '/games/snake_ladder/Cash-3.png',
  cash4:    '/games/snake_ladder/Cash-4.png',
  victory:  '/games/snake_ladder/Victory_Trophy.png',
  defeat:   '/games/snake_ladder/Defeat_Medal.png',
  kicked:   '/games/snake_ladder/Kicked_Boot.png',
} as const;
// Every lobby/result image, flattened once (stable identity) for the
// asset preloader → the themed loading screen stays up until they decode.
const SL_PRELOAD_URLS = flattenImageList(IMG);
const CASH_IMAGES = [IMG.cash1, IMG.cash2, IMG.cash3, IMG.cash4];
const BET_TIERS = [10, 50, 100, 250, 500, 1000];
const PLATFORM_FEE_PCT = 0; // platform fee ABOLISHED (H5/H7)
const TURN_SECONDS = 15;

function fmt(n: number) { return Math.floor(n).toLocaleString('en-IN'); }

function avatarFor(p?: { avatarUrl?: string | null; color?: Color }) {
  return p?.avatarUrl || `/games/snake_ladder/RC_Player_${p?.color || 'blue'}.png`;
}

// ─── Component ───────────────────────────────────────────────────
export default function SnakeLadderPage() {
  const nav = useNavigate();
  const toast = useToast();
  const wsRef = useRef<SnakeLadderWS | null>(null);
  const { user } = useAuth();
  const myUserId = user ? String(user.id) : null;
  const walletState = useWallet();
  const wallet = walletState?.balance ?? 0;

  const [wsStatus, setWsStatus] = useState<WSStatus>('connecting');
  const [phase, setPhase]       = useState<Phase>('lobby');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Cross-game concurrent-match modal — opened when the server rejects
  // join_queue with `concurrent_match_limit` (the user is already in a
  // Ludo/Chess/Carrom/… match). `concBlocking` carries the blocking
  // match list the modal renders into continue/quit rows.
  const [concOpen, setConcOpen] = useState(false);
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundOn, setSoundOn]   = useState(true);
  const [quitConfirm, setQuitConfirm] = useState(false);
  // Auto-roll (same as Ludo): when ON, the dice rolls itself on your turn.
  const [autoRoll, setAutoRoll] = useState(false);
  // Themed loading screen — stays up until BOTH the WS has connected at
  // least once (wsReady, one-shot so it never re-covers an active match on
  // a reconnect) AND every lobby/result image has decoded (assetStatus).
  const [wsReady, setWsReady] = useState(false);
  const assetStatus = useAssetPreloader(SL_PRELOAD_URLS, 600);

  // In-match voice chat (WS-relayed PCM, same as Ludo). Mic on by
  // default to match Ludo; user can mute via the settings menu.
  const [micMuted, setMicMuted]       = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  const voiceRef = useRef<WSVoice | null>(null);
  const micMutedRef = useRef(micMuted);
  const listenMutedRef = useRef(listenMuted);
  useEffect(() => { micMutedRef.current = micMuted; }, [micMuted]);
  useEffect(() => { listenMutedRef.current = listenMuted; }, [listenMuted]);

  // Server config (bet tiers + fee). Fetched on mount so the lobby
  // always matches what the server actually accepts — admin edits to
  // BET_TIERS/PLATFORM_FEE_PERCENT flow through without a redeploy.
  // Falls back to sensible defaults if the fetch fails.
  const [betTiers, setBetTiers] = useState<number[]>(BET_TIERS);
  const [feePct, setFeePct]     = useState<number>(PLATFORM_FEE_PCT);
  // Strike limit (admin-configurable) — drives how many strike pips each
  // player panel shows. Falls back to 5 (current default) before /config.
  const [maxStrikes, setMaxStrikes] = useState<number>(5);

  // Lobby picks
  const [bet, setBet]         = useState<number>(0);
  const [players, setPlayers] = useState<2 | 4>(2);

  // Queue
  const [queueSize, setQueueSize]   = useState(0);
  const [queueProfiles, setQueueProfiles] = useState<Array<{ userId: string; name: string; avatarUrl: string | null; is_bot: boolean }>>([]);
  const [botActivateAt, setBotActivateAt] = useState<number | null>(null);

  // Match
  const [board, setBoard] = useState<{ snakes: SnakeData[]; ladders: LadderData[] }>({ snakes: [], ladders: [] });
  const [state, setState] = useState<EngineState | null>(null);
  const [eligibleTokens, setEligibleTokens] = useState<('A' | 'B')[] | null>(null);
  const [turnDeadline, setTurnDeadline]     = useState<number | null>(null);
  const [lastRoll, setLastRoll] = useState<{ value: number; byPlayerId: number } | null>(null);

  // ── Move-animation state ──────────────────────────────────────────
  // During a move we DON'T snap the board to the server's final state.
  // Instead we replay the server's animation script step-by-step:
  //   • displayPos overrides a token's rendered square while it hops
  //   • moveMs controls the per-step transition duration (short for
  //     square hops, longer for snake/ladder slides)
  //   • animating locks the dice/pick controls so you can't roll mid-move
  //   • animChain serialises overlapping moves (e.g. bot extra-turns)
  const [displayPos, setDisplayPos] = useState<Record<string, number>>({});
  const [moveMs, setMoveMs]         = useState<number>(170);
  const [animating, setAnimating]   = useState(false);
  const [animatingKey, setAnimatingKey] = useState<string | null>(null);
  const animChainRef = useRef<Promise<void>>(Promise.resolve());
  const boardSvgRef  = useRef<SVGSVGElement>(null);

  // Result
  const [result, setResult] = useState<{ kind: 'win' | 'loss' | 'kick'; winner: EnginePlayer | null; pot: number; payout: number; platformFee: number } | null>(null);

  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // body class hides the site nav + locks scroll (mirrors ludo-active)
  useEffect(() => {
    document.body.classList.add('sl-active');
    return () => { document.body.classList.remove('sl-active'); };
  }, []);

  useEffect(() => { setSfxEnabled(soundOn); }, [soundOn]);
  useEffect(() => {
    installAudioUnlock();
    void refreshWallet();
    // Pull the authoritative bet tiers + fee from the server so the
    // lobby never offers a stake the server rejects (bad_bet).
    (async () => {
      try {
        const cfg = await api.get<{ betTiers: number[]; platformFeePercent: number; maxStrikes?: number }>('/snake_ladder/config');
        if (Array.isArray(cfg?.betTiers) && cfg.betTiers.length) {
          setBetTiers(cfg.betTiers);
          setBet((b) => (cfg.betTiers.includes(b) ? b : cfg.betTiers[0]));
        }
        if (typeof cfg?.platformFeePercent === 'number') setFeePct(cfg.platformFeePercent);
        if (typeof cfg?.maxStrikes === 'number' && cfg.maxStrikes > 0) setMaxStrikes(cfg.maxStrikes);
      } catch { /* keep fallback defaults */ }
    })();
  }, []);

  // ─── Boot: connect WS ──────────────────────────────────────────
  useEffect(() => {
    const ws = new SnakeLadderWS();
    wsRef.current = ws;
    const offStatus = ws.onStatus(setWsStatus);
    const offMsg    = ws.on((msg) => routeMessage(msg));
    ws.connect();
    return () => { offStatus(); offMsg(); ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot: once the WS connects, the loading screen can lift (and stays
  // lifted — a later reconnect must NOT slap the loader back over a live
  // match).
  useEffect(() => { if (wsStatus === 'open') setWsReady(true); }, [wsStatus]);

  // ─── Incoming WS dispatcher ────────────────────────────────────
  function routeMessage(msg: any) {
    switch (msg.type) {
      case 'authenticated': break;
      case 'pong': break;
      case 'online_count': setOnline(msg.count ?? 0); break;
      case 'queue_joined':
        setPhase('queue');
        setQueueSize(msg.size ?? 1);
        if (Array.isArray(msg.profiles)) setQueueProfiles(msg.profiles);
        if (typeof msg.botActivateAt === 'number') setBotActivateAt(msg.botActivateAt);
        break;
      case 'queue_size':
        setQueueSize(msg.size ?? 0);
        if (Array.isArray(msg.profiles)) setQueueProfiles(msg.profiles);
        if (typeof msg.botActivateAt === 'number') setBotActivateAt(msg.botActivateAt);
        break;
      case 'queue_left':
        setPhase('lobby'); setQueueSize(0); setQueueProfiles([]); setBotActivateAt(null);
        break;
      case 'match_start': {
        setBoard({ snakes: msg.board.snakes, ladders: msg.board.ladders });
        setState(msg.state);
        setResult(null);
        setLastRoll(null);
        setEligibleTokens(null);
        // Spin up voice chat for this match — find my seat colour, then
        // start the WS-relayed PCM mesh (mirrors Ludo). Mic was prewarmed
        // inside the Play click so the browser grants access.
        startVoice(msg.state);
        // VS cinematic, then play.
        setPhase('vs');
        setTimeout(() => { if (phaseRef.current === 'vs') setPhase('playing'); }, 3000);
        break;
      }
      case 'state_sync':
        setState(msg.state);
        if (msg.board) setBoard({ snakes: msg.board.snakes, ladders: msg.board.ladders });
        if (phaseRef.current === 'lobby' || phaseRef.current === 'queue') setPhase('playing');
        break;
      case 'turn': {
        // Deferred behind any in-flight move animation so the next
        // turn only begins once the previous move has finished playing.
        const m = msg;
        enqueue(() => {
          if (m.state) setState(m.state);
          else setState((s) => (s ? { ...s, awaiting: 'roll' } : s));
          setTurnDeadline(m.deadline ?? null);
          setEligibleTokens(null);
          setLastRoll(null);
          if (m.userId && myUserId && String(m.userId) === myUserId) playYourTurnChime();
        });
        break;
      }
      case 'dice_rolled':
        setLastRoll({ value: msg.roll, byPlayerId: msg.byPlayerId });
        playDiceRollSound();
        break;
      case 'awaiting_token_pick':
        if (msg.state) setState(msg.state);
        else setState((s) => (s ? { ...s, awaiting: 'token_pick' } : s));
        setEligibleTokens(msg.eligibleTokens);
        break;
      case 'token_moved': {
        setEligibleTokens(null);
        const m = msg;
        // Pin the moving token at its starting square IMMEDIATELY so a
        // late 'turn' commit can't flash it to the destination before
        // the hop animation runs.
        const mover = m.state?.players?.find((p: EnginePlayer) => p.playerId === m.byPlayerId);
        const firstFrom = Array.isArray(m.animations) && m.animations[0] ? m.animations[0].from : undefined;
        if (mover && firstFrom != null && m.animations[0]?.tokenLetter) {
          const key = `${mover.color}-${m.animations[0].tokenLetter}`;
          setDisplayPos((prev) => ({ ...prev, [key]: firstFrom }));
        }
        enqueue(() => playMoveAnimation(m));
        break;
      }
      case 'voice_chunk':
        voiceRef.current?.onRemoteChunk(msg.from, msg.pcm);
        break;
      case 'roll_voided': break;
      case 'strike': break;
      case 'forfeit': {
        const m = msg;
        enqueue(() => { if (m.state) setState(m.state); });
        break;
      }
      case 'connection_status':
        setState((s) => s ? {
          ...s,
          players: s.players.map((p) => p.playerId === msg.playerId ? { ...p, connected: msg.connected } : p),
        } : s);
        break;
      case 'match_ended': {
        // Chain behind the final move animation so the winning token is
        // seen reaching home BEFORE the result screen takes over.
        const m = msg;
        enqueue(async () => {
          const st: EngineState | undefined = m.state;
          if (st) setState(st);
          await sleep(500); // let the home celebration land
          const w = st?.players?.find((p) => p.userId === m.winnerUserId) || null;
          const iWon = !!(w && myUserId && w.userId === myUserId);
          const meSeat = st?.players?.find((p) => p.userId === myUserId) || null;
          const kicked = meSeat?.seatStatus === 'forfeited';
          const kind: 'win' | 'loss' | 'kick' = iWon ? 'win' : kicked ? 'kick' : 'loss';
          setResult({ kind, winner: w, pot: m.pot ?? 0, payout: m.payoutToWinner ?? 0, platformFee: m.platformFee ?? 0 });
          setPhase('result');
          stopVoice();
          stopUrgencyBeep();
          if (kind === 'win') playWinFanfare();
          else if (kind === 'kick') playKickSound();
          else playLossTone();
          void refreshWallet();
        });
        break;
      }
      case 'error':
        if (msg.code === 'concurrent_match_limit') {
          // User is already in another game's match/queue. Roll back to
          // the lobby and pop the shared continue/quit modal instead of
          // a transient toast (parity with Ludo/Chess/Carrom).
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          setPhase('lobby');
          setQueueSize(0);
          break;
        }
        if (msg.code === 'bet_limit') {
          setErrorMsg(msg.message || 'This bet is above your limit — contact your agent.');
          setTimeout(() => setErrorMsg(null), 3500);
          break;
        }
        setErrorMsg(`${msg.code}${msg.message ? `: ${msg.message}` : ''}`);
        setTimeout(() => setErrorMsg(null), 3500);
        break;
      default: break;
    }
  }

  const [online, setOnline] = useState<number>(0);

  // Serialise board mutations behind the move-animation queue so the
  // next turn/result never races ahead of the move being shown.
  function enqueue(fn: () => void | Promise<void>) {
    animChainRef.current = animChainRef.current.then(fn).catch(() => {});
  }

  // Replay the server's per-step animation script by imperatively
  // animating the token's SVG node (Web Animations API), then committing
  // the authoritative server state. Techniques ported from the reference:
  //   • JUMP: each square arcs up-and-over via a raised mid-keyframe
  //     (sine-style hop) rather than a flat slide.
  //   • SNAKE BITE: the token walks through the snake's actual body
  //     curve points (snakePathPoints) down to the tail.
  //   • LADDER: a smooth eased climb straight to the top.
  async function playMoveAnimation(m: any) {
    const st: EngineState | undefined = m.state;
    const mover = st?.players?.find((p) => p.playerId === m.byPlayerId);
    const anims: Array<any> = Array.isArray(m.animations) ? m.animations : [];
    if (!mover || anims.length === 0) { if (st) setState(st); return; }
    const color = mover.color;
    const letter = anims[0].tokenLetter;
    const key = `${color}-${letter}`;

    setAnimating(true);
    setAnimatingKey(key); // freezes that token's CSS transition

    await sleep(DICE_HOLD); // let the rolled dice value register first

    const node = boardSvgRef.current?.querySelector(
      `[data-tok="${(window as any).CSS?.escape ? CSS.escape(key) : key}"]`,
    ) as SVGGElement | null;

    // Keep the egg at its big "running" size for the whole move so it (and
    // its legs) stay clearly visible while it travels squares 2..99. It
    // settles to its destination slot scale on the React commit afterwards.
    const tr = (x: number, y: number) => `translate(${x}px, ${y}px) scale(${EGG_RUN_SCALE})`;
    const hold = (x: number, y: number) => { if (node) node.style.transform = tr(x, y); };
    const run = async (keyframes: Keyframe[], duration: number, easing: string) => {
      if (!node) { await sleep(duration); return; }
      const anim = node.animate(keyframes as any, { duration, easing, fill: 'forwards' });
      try { await (anim as any).finished; } catch { /* cancelled */ }
      // Bake the final transform into inline style and DROP the animation.
      // A lingering fill:forwards animation outranks inline/React styles in
      // the cascade — if left active it pins the egg at the big run scale,
      // so a homed egg stays huge in the nest instead of shrinking to its
      // small home-slot size. commitStyles() preserves the visual, cancel()
      // hands control back to React's resting transform.
      try { anim.commitStyles(); } catch { /* ignore */ }
      try { anim.cancel(); } catch { /* ignore */ }
    };

    let cur = centerOf(anims[0].from);
    for (const a of anims) {
      if (a.kind === 'hop' || a.kind === 'bounce') {
        const dir = a.to >= a.from ? 1 : -1;
        const squares: number[] = [];
        for (let p = a.from + dir; dir > 0 ? p <= a.to : p >= a.to; p += dir) squares.push(p);
        for (let i = 0; i < squares.length; i++) {
          const c = centerOf(squares[i]);
          const mx = (cur.x + c.x) / 2;
          const my = (cur.y + c.y) / 2 - HOP_HEIGHT;
          playJumpSound();
          await run([
            { transform: tr(cur.x, cur.y) },
            { transform: tr(mx, my), offset: 0.5 },
            { transform: tr(c.x, c.y) },
          ], HOP_MS, 'ease-in-out');
          hold(c.x, c.y);
          cur = c;
        }
      } else if (a.kind === 'snake') {
        // Derive the curve shape the SAME deterministic way the snake was
        // drawn (snakeKind on head+tail) — no array lookup that could miss
        // and send the token down the wrong curve.
        const kind = snakeKind(a.from, a.to);
        const pts = snakePathPoints(a.from, a.to, kind, 64);
        playSnakeHiss();   // snake eats the egg → hiss
        await run(pts.map((pt) => ({ transform: tr(pt.x, pt.y) })), SLIDE_MS, 'ease-in');
        const end = centerOf(a.to); hold(end.x, end.y); cur = end;
      } else if (a.kind === 'ladder') {
        const c = centerOf(a.to);
        playSafeZoneSound();
        await run([
          { transform: tr(cur.x, cur.y) },
          { transform: tr(c.x, c.y) },
        ], SLIDE_MS, 'cubic-bezier(0.22, 1, 0.36, 1)');
        hold(c.x, c.y); cur = c;
      } else if (a.kind === 'home') {
        playChickenCluck();   // the mama hen clucks as her egg reaches the nest
        await sleep(700);
      }
    }

    // Commit authoritative state, clear the per-token override, then
    // restore the CSS transition one frame later (token already at final).
    if (st) setState(st);
    setDisplayPos((prev) => {
      const next = { ...prev };
      for (const a of anims) delete next[`${color}-${a.tokenLetter}`];
      return next;
    });
    setAnimating(false);
    requestAnimationFrame(() => setAnimatingKey(null));
  }

  // ─── Derived ───────────────────────────────────────────────────
  const me = useMemo(() => {
    if (!state || !myUserId) return null;
    return state.players.find((p) => p.userId === myUserId) || null;
  }, [state, myUserId]);
  const currentPlayer = state ? state.players[state.currentPlayerIndex] : undefined;
  const isMyTurn = !!(me && currentPlayer && me.userId === currentPlayer.userId);

  const pot = state ? bet * state.players.length : bet * players;
  const potPayout = Math.floor(pot * (100 - feePct) / 100);

  // urgency beep in last 5s of my turn
  useEffect(() => {
    if (!isMyTurn || !turnDeadline) { stopUrgencyBeep(); return; }
    const id = setInterval(() => {
      const left = (turnDeadline - Date.now()) / 1000;
      if (left <= 5 && left > 0) startUrgencyBeep(); else stopUrgencyBeep();
    }, 300);
    return () => { clearInterval(id); stopUrgencyBeep(); };
  }, [isMyTurn, turnDeadline]);

  // ─── Auto-roll (mirrors Ludo) ──────────────────────────────────
  // Turn it off whenever we leave an active game so it doesn't silently
  // carry into the next match.
  useEffect(() => { if (phase !== 'playing') setAutoRoll(false); }, [phase]);
  // When AUTO is on and it becomes my turn to roll, fire the roll after a
  // short beat (700ms) so the player sees whose turn it is. A forced single
  // egg is then auto-moved by the engine; a real A/B choice still waits for
  // a tap (exactly like Ludo's auto-roll + forced-move behaviour).
  useEffect(() => {
    if (phase !== 'playing' || !autoRoll || !isMyTurn || animating) return;
    if (!state || state.awaiting !== 'roll') return;
    const t = setTimeout(() => sendRoll(), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, autoRoll, isMyTurn, animating, state?.awaiting, state?.turnId]);

  // ─── Voice chat ────────────────────────────────────────────────
  function startVoice(st: EngineState) {
    try { voiceRef.current?.stop(); } catch {}
    voiceRef.current = null;
    const mine = st.players.find((p) => p.userId === myUserId);
    if (!mine) return;
    const v = new WSVoice(mine.color, (m) => { wsRef.current?.send(m); });
    if (micMutedRef.current) v.setMuted(true);
    if (listenMutedRef.current) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((okStarted) => {
      // Mic denied → reflect as muted so the settings glyph is honest.
      if (!okStarted) setMicMuted(true);
    }).catch(() => setMicMuted(true));
  }
  function stopVoice() {
    try { voiceRef.current?.stop(); } catch {}
    voiceRef.current = null;
  }
  async function toggleMic() {
    const v = voiceRef.current;
    const next = !micMuted;
    setMicMuted(next);
    if (!v) return;
    if (!next && !v.hasLocalStream()) {
      // Re-acquire mic on an explicit unmute gesture (first grant may
      // have been denied at match start).
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        await v.attachLocalStream(stream);
      } catch { setMicMuted(true); return; }
    }
    v.setMuted(next);
  }
  function toggleListen() {
    const next = !listenMuted;
    setListenMuted(next);
    voiceRef.current?.setListenMuted(next);
    if (!next) voiceRef.current?.kickAudioPlayback();
  }

  // Stop voice when the component unmounts.
  useEffect(() => () => { stopVoice(); }, []);

  // ─── Actions ───────────────────────────────────────────────────
  function doPlay(b: number) {
    primeAudio();
    // Prewarm the mic inside this user gesture so the browser grants
    // access for in-match voice (Safari/mobile require a gesture).
    if (!micMuted) { void WSVoice.prewarmMic(); }
    setErrorMsg(null);
    // Only pre-block when we KNOW the balance is short. wallet===0 most
    // often means the async refreshWallet hasn't resolved yet — in that
    // case let the server's join_queue do the authoritative check rather
    // than falsely rejecting a fast click.
    if (b > 0 && wallet > 0 && wallet < b) { setErrorMsg('Not enough balance for this stake.'); return; }
    setBet(b);
    wsRef.current?.send(joinQueueMsg(b, players));
  }
  function cancelQueue() { playTap(); wsRef.current?.send(leaveGameMsg()); setPhase('lobby'); }
  function sendRoll() { primeAudio(); wsRef.current?.send(rollDiceMsg()); }
  function sendPick(letter: 'A' | 'B') { wsRef.current?.send(pickTokenMsg(letter)); }
  function backOrQuit() {
    playTap();
    if (phase === 'playing' || phase === 'vs') setQuitConfirm(true);
    else nav('/');
  }
  function confirmQuit() {
    wsRef.current?.send(leaveGameMsg());
    setQuitConfirm(false);
    nav('/');
  }

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="sl-root fixed inset-0 z-[9999]">
      {/* Themed loading screen — covers everything until the WS is up and
          every lobby/result image has decoded (parity with Ludo et al). */}
      <GameLoadingScreen
        visible={!wsReady || !assetStatus.ready}
        theme="snake_ladder"
        progress={assetStatus.progress}
      />
      <ConcurrentMatchModal
        open={concOpen}
        blocking={concBlocking}
        walletBalance={wallet}
        onResolved={() => {
          setConcOpen(false);
          setConcBlocking(null);
          toast.push('success', 'Active matches resolved — try again', 2000);
        }}
        onCancel={() => setConcOpen(false)}
      />
      <div className="app-container shadow-2xl">
        <GameSettingsMenu
          open={settingsOpen}
          soundOn={soundOn}
          micMuted={micMuted}
          listenMuted={listenMuted}
          voiceEnabled={phase === 'playing'}
          onToggleSound={() => { playTap(); setSoundOn((s) => !s); }}
          onToggleMic={() => { playTap(); void toggleMic(); }}
          onToggleListen={() => { playTap(); toggleListen(); }}
          onExit={() => { playTap(); setSettingsOpen(false); if (phase === 'playing' || phase === 'vs') setQuitConfirm(true); else nav('/'); }}
          onClose={() => setSettingsOpen(false)}
          exitLabel={(phase === 'playing' || phase === 'vs') ? 'Exit Game' : 'Exit to Home'}
          anchor="left"
        />

        <TopBar
          walletAmt={wallet}
          onSettings={() => { playTap(); setSettingsOpen((o) => !o); }}
          onBack={backOrQuit}
          potPayout={phase === 'playing' ? potPayout : null}
          cashBannerImg={phase === 'playing' ? CASH_IMAGES[Math.min(Math.max(0, betTiers.indexOf(bet)), CASH_IMAGES.length - 1)] || CASH_IMAGES[0] : null}
        />

        <div className="status-container">
          <div className={`status-box bg-gray-700 text-white ${wsStatus === 'open' ? 'hidden' : ''}`}>
            {wsStatus === 'lost' ? 'Connection Lost! Reconnecting…'
              : wsStatus === 'reconnecting' ? 'Reconnecting to Server…'
              : 'Connecting to Server…'}
          </div>
        </div>

        {errorMsg && (
          <div className="sl-error-toast">{errorMsg}</div>
        )}

        {/* ===== LOBBY ===== */}
        {phase === 'lobby' && (
          <SetupScreen
            authReady={wsStatus === 'open'}
            onlinePlayers={online}
            betTiers={betTiers}
            feePct={feePct}
            selectedBet={bet}
            selectedPlayers={players}
            onSelectPlayers={(n) => { playTap(); setPlayers(n); }}
            onSelectBet={(b) => { playTap(); doPlay(b); }}
          />
        )}

        {/* ===== QUEUE ===== */}
        {phase === 'queue' && (
          <WaitingModal
            current={queueSize}
            required={players}
            bet={bet}
            players={players}
            botActivateAt={botActivateAt}
            profiles={queueProfiles}
            yourUserId={myUserId}
            myAvatarUrl={user?.profile_image_url || null}
            onCancel={cancelQueue}
          />
        )}

        {/* ===== VS INTRO ===== */}
        {phase === 'vs' && state && (
          <VsScreen players={state.players} myUserId={myUserId} pot={pot} payout={potPayout} />
        )}

        {/* ===== GAME ===== */}
        {phase === 'playing' && state && (
          <GameScreen
            state={state}
            board={board}
            myUserId={myUserId}
            isMyTurn={isMyTurn}
            eligibleTokens={eligibleTokens}
            lastRoll={lastRoll}
            turnDeadline={turnDeadline}
            displayPos={displayPos}
            moveMs={moveMs}
            animating={animating}
            animatingKey={animatingKey}
            svgRef={boardSvgRef}
            onRoll={sendRoll}
            onPickToken={sendPick}
            autoRoll={autoRoll}
            onToggleAutoRoll={() => { playTap(); setAutoRoll((v) => !v); }}
            maxStrikes={maxStrikes}
          />
        )}

        {/* ===== RESULT ===== */}
        {phase === 'result' && result && (
          <ResultScreen
            kind={result.kind}
            amount={result.kind === 'win' ? result.payout : result.pot}
            winnerName={result.winner?.displayName}
            platformFee={result.platformFee}
            onHome={() => nav('/')}
            onNewMatch={() => { setResult(null); setState(null); setPhase('lobby'); }}
          />
        )}

        {/* ===== QUIT CONFIRM ===== */}
        {quitConfirm && (
          <QuitConfirm amount={potPayout} onStay={() => setQuitConfirm(false)} onQuit={confirmQuit} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TopBar
// ─────────────────────────────────────────────────────────────────
function TopBar({ walletAmt, onSettings, onBack, potPayout, cashBannerImg }: {
  walletAmt: number; onSettings: () => void; onBack: () => void;
  potPayout: number | null; cashBannerImg: string | null;
}) {
  return (
    <div
      className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#0a2a18] to-[#13602f] shadow-md z-[150] w-full"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onBack} aria-label="Back"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white">
          <Icon name="arrow_back" size={22} filled />
        </button>
        <button onClick={onSettings} aria-label="Settings"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform">
          <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
        </button>
      </div>

      {potPayout != null && cashBannerImg && (
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="bg-gradient-to-b from-[#0c2c1a] to-[#13602f] rounded-lg flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative">
            <img src={cashBannerImg} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="" />
            <div className="flex flex-col items-start leading-none mr-1 sm:mr-2">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-white/70">WIN</span>
              <span className="text-white font-bold text-base sm:text-lg tracking-wide tabular-nums mt-0.5">{fmt(potPayout)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-b from-[#0c2c1a] to-[#13602f] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative">
        <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
        <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide">{fmt(walletAmt)}</span>
        <button className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform">
          <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lobby
// ─────────────────────────────────────────────────────────────────
function SetupScreen({
  authReady, onlinePlayers, betTiers, feePct, selectedBet, selectedPlayers, onSelectPlayers, onSelectBet,
}: {
  authReady: boolean; onlinePlayers: number; betTiers: number[]; feePct: number;
  selectedBet: number; selectedPlayers: 2 | 4;
  onSelectPlayers: (n: 2 | 4) => void; onSelectBet: (n: number) => void;
}) {
  return (
    <div id="setup-screen" className="flex flex-col w-full h-full relative transition-all duration-300 flex-grow goldern_border rounded-none sm:rounded-b-3xl" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {!authReady && (
        <div id="lobby-loader" className="lobby-loader">
          <div className="ludo-spinner" aria-hidden="true">
            <div className="ludo-spinner-token red" />
            <div className="ludo-spinner-token green" />
            <div className="ludo-spinner-token yellow" />
            <div className="ludo-spinner-token blue" />
          </div>
          <div className="lobby-loader-text">Setting up lobby</div>
          <div className="lobby-loader-bar" />
        </div>
      )}
      {authReady && (
        <div id="lobby-content" className="flex flex-col flex-grow w-full">
          <div className="flex-grow flex flex-col justify-center items-center px-4 sm:px-6 min-h-[150px]">
            <img
              src={IMG.logo}
              className="w-full h-full object-contain drop-shadow-2xl"
              style={{ maxHeight: 250 }}
              alt="Snake & Ladder Cash Logo"
            />
            <div className="mt-4 text-[#dfff7a] text-[10px] sm:text-xs font-bold tracking-widest bg-black/50 px-4 py-2 rounded-full border border-[#bc9648] flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.8)]" aria-hidden />
              <span>{onlinePlayers} PLAYERS ONLINE</span>
            </div>
          </div>
          <div className="px-4 sm:px-5 mt-auto flex flex-col gap-3 w-full pb-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-2 sm:mb-4">
              <PawnButton n={2} img={IMG.pawns2} selected={selectedPlayers === 2} onClick={() => onSelectPlayers(2)} />
              <PawnButton n={4} img={IMG.pawns4} selected={selectedPlayers === 4} onClick={() => onSelectPlayers(4)} />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {betTiers.map((val, i) => {
                const winAmt = Math.floor(val * selectedPlayers * (100 - feePct) / 100);
                return (
                  <button key={val} data-val={val} onClick={() => onSelectBet(val)}
                    className={['cash-btn relative w-full py-3 sm:h-[95px] rounded-xl border-[3px] border-[#bc9648]',
                      'bg-gradient-to-b from-[#0c2c1a] to-[#04140b] flex flex-row items-center justify-between',
                      'pl-3 sm:pl-4 pr-1 sm:pr-2 shadow-[0_6px_15px_rgba(0,0,0,0.6)]',
                      selectedBet === val ? 'selected' : ''].join(' ')}>
                    <div className="flex flex-col items-start justify-center pt-1 pointer-events-none">
                      <span className={'text-white font-black leading-none text-shadow-heavy tracking-tighter ' + (val === 0 ? 'text-[22px] sm:text-[30px]' : 'text-[32px] sm:text-[42px]')}>{val === 0 ? 'FREE' : val}</span>
                      <span className="cash-win-label text-[#dfff7a] font-bold text-[10px] sm:text-sm tracking-widest text-shadow-heavy mt-1">{val === 0 ? 'PLAY FOR FUN' : `WIN ${winAmt}`}</span>
                    </div>
                    <img src={CASH_IMAGES[i] || CASH_IMAGES[CASH_IMAGES.length - 1]} className="w-[50px] h-[50px] sm:w-[75px] sm:h-[75px] object-contain drop-shadow-lg pointer-events-none" alt="" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PawnButton({ n, img, selected, onClick }: { n: 2 | 4; img: string; selected: boolean; onClick: () => void }) {
  return (
    <button data-players={n} onClick={onClick}
      className={['pawn-btn relative w-full aspect-[4/3] rounded-xl border-[3px] border-[#bc9648]',
        'bg-gradient-to-b from-[#0c2c1a] to-[#04140b] flex flex-col justify-center items-center',
        'shadow-[0_6px_15px_rgba(0,0,0,0.6)]', selected ? 'selected' : ''].join(' ')}>
      <div className="flex items-center justify-center w-full px-2 mt-1 gap-1">
        <span className="text-[#e8c66e] text-5xl sm:text-6xl font-black text-shadow-heavy leading-none">{n}</span>
        <img src={img} className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] object-contain drop-shadow-xl" alt={`${n} Pawns`} />
      </div>
      <span className={`font-bold text-[12px] sm:text-[15px] tracking-widest mt-1 sm:mt-2 text-shadow-heavy ${selected ? 'text-[#dfff7a]' : 'text-[#bc9648]'}`}>
        {n === 2 ? 'TWO PLAYER' : 'FOUR PLAYER'}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Waiting Modal
// ─────────────────────────────────────────────────────────────────
function WaitingModal({
  current, required, bet, players, botActivateAt, onCancel, profiles, yourUserId, myAvatarUrl,
}: {
  current: number; required: number; bet: number; players: 2 | 4;
  botActivateAt: number | null; onCancel: () => void;
  profiles: Array<{ userId: string; name: string; avatarUrl: string | null; is_bot: boolean }>;
  yourUserId: string | null; myAvatarUrl: string | null;
}) {
  const needed = Math.max(0, required - current);
  const label = needed === 1 ? 'more player' : 'more players';
  const slotColors: Color[] = players === 2 ? ['blue', 'green'] : ['blue', 'red', 'green', 'yellow'];
  const [flipIdx, setFlipIdx] = useState(0);
  const [dotPhase, setDotPhase] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setFlipIdx((i) => (i + 1) % 4), 450);
    const d = setInterval(() => setDotPhase((p) => (p + 1) % 3), 400);
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => { clearInterval(t); clearInterval(d); clearInterval(tick); };
  }, []);
  const dotsText = dotPhase === 0 ? '.' : dotPhase === 1 ? '..' : '...';
  const secsToBot = botActivateAt ? Math.max(0, Math.ceil((botActivateAt - now) / 1000)) : null;
  const avatarChoices = ['red', 'green', 'yellow', 'blue'].map((c) => `/games/snake_ladder/RC_Player_${c}.png`);

  return (
    <div id="waiting-modal" className="absolute inset-0 z-[160] bg-black/85 flex justify-center items-center transition-opacity duration-300">
      <div className="relative w-[85%] max-w-[340px] border-[3px] border-[#d4af37] bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#14532d] via-[#0a2a18] to-[#041209] rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.9)] flex flex-col items-center">
        <button onClick={onCancel} className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform">✕</button>
        <img src={IMG.logo} className="w-28 h-auto object-contain drop-shadow-2xl mb-1" alt="Snake & Ladder Cash Logo" />
        <h2 className="text-gold-gradient text-xl font-black tracking-widest gold-text-shadow mb-2 text-center uppercase">
          <span className="block text-sm tracking-[0.3em] text-[#bc9648]">WAITING FOR</span>
          <span className="block text-6xl md:text-7xl font-black leading-none my-1 text-shadow-heavy" style={{ color: '#dfff7a', textShadow: '0 0 14px rgba(223,255,122,0.45)' }}>{needed}</span>
          <span className="block text-[11px] tracking-[0.25em] text-[#f8e7b3] uppercase">{label}</span>
        </h2>
        <div id="wait-slots-row" className="wait-slots-row">
          {slotColors.map((c, i) => {
            const locked = i < current;
            const p = locked ? (profiles[i] || null) : null;
            const isMe = locked && ((yourUserId && p && p.userId === yourUserId) || (!p && i === 0));
            let avatar: string;
            if (locked && p) avatar = p.avatarUrl || `/games/snake_ladder/RC_Player_${c}.png`;
            else if (locked && isMe && myAvatarUrl) avatar = myAvatarUrl;
            else if (locked) avatar = `/games/snake_ladder/RC_Player_${c}.png`;
            else avatar = avatarChoices[(flipIdx + i) % avatarChoices.length];
            const nameLabel = locked ? (isMe ? 'YOU' : (p?.name || c.toUpperCase())) : dotsText;
            return (
              <div key={c} className={['wait-slot', locked ? 'locked' : 'searching', locked && isMe ? 'me' : ''].join(' ')} style={{ ['--slot-color' as any]: COLOR_HEX[c] }}>
                {locked && isMe && <div className="wait-slot-you">YOU</div>}
                <div className="wait-slot-ring"><img src={avatar} alt="" /></div>
                <div className="wait-slot-label">{nameLabel}</div>
              </div>
            );
          })}
        </div>
        <div className="w-full bg-[#06170e] border border-[#bc9648] rounded-lg py-2 px-3 mb-3 shadow-inner flex justify-around items-center">
          <div className="flex items-center gap-2">
            <img src={players === 2 ? IMG.pawns2 : IMG.pawns4} className="w-7 h-7 object-contain" alt="" />
            <span className="text-[#dfff7a] font-bold text-[10px] tracking-wider">{players} PLAYERS</span>
          </div>
          <span className="text-[#d4af37] text-[10px] font-black tracking-widest">FOR</span>
          <div className="flex items-center gap-2">
            <img src={IMG.cash2} className="w-7 h-7 object-contain" alt="" />
            <span className="text-[#7ae2ff] font-bold text-[10px] tracking-wider">{fmt(bet)}</span>
          </div>
        </div>
        {secsToBot != null && secsToBot > 0 && (
          <div className="text-[11px] tracking-widest text-[#f8e7b3] mb-2 text-center">MATCH STARTS IN <span className="text-[#dfff7a] font-bold">{secsToBot}s</span></div>
        )}
        <button onClick={onCancel} className="w-full py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest text-shadow-heavy shadow-md hover:brightness-110 active:scale-[0.98] transition-all">CANCEL</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// VS Intro
// ─────────────────────────────────────────────────────────────────
function VsScreen({ players, myUserId, pot, payout }: {
  players: EnginePlayer[]; myUserId: string | null; pot: number; payout: number;
}) {
  const mine = players.find((p) => p.userId === myUserId) || players[0];
  const others = players.filter((p) => p.playerId !== mine.playerId);
  const sparks = useMemo(() => Array.from({ length: 12 }).map((_, i) => ({
    left: Math.random() * 100, bottom: -Math.random() * 20, delay: Math.random() * 2, duration: 2.4 + Math.random() * 1.6, key: i,
  })), []);
  return (
    <div id="vs-screen" className="overlay-screen themed z-[250]">
      <div id="vs-sparks" className="vs-sparks">
        {sparks.map((s) => (
          <span key={s.key} className="vs-spark" style={{ left: `${s.left}%`, bottom: `${s.bottom}%`, animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s` }} />
        ))}
      </div>
      <div className="vs-banner">Match Found</div>
      <div id="vs-stage" className="vs-stage">
        <VsCard player={mine} side="left-in" isMe />
        <div className="vs-divider"><span>V/S</span></div>
        {others.map((p, i) => <VsCard key={p.playerId} player={p} side={i % 2 === 0 ? 'right-in' : 'left-in'} />)}
      </div>
      <div className="vs-pot">POT<span className="ml-2 text-[#fff6d6]">{fmt(pot)} · WIN{fmt(payout)}</span></div>
    </div>
  );
}
function VsCard({ player, side, isMe }: { player: EnginePlayer; side: string; isMe?: boolean }) {
  return (
    <div className={`vs-card ${isMe ? 'me' : side}`} style={{ ['--vs-color' as any]: COLOR_HEX[player.color] }}>
      {isMe && <div className="vs-you-badge">YOU</div>}
      <div className="vs-card-ring"><img src={avatarFor(player)} alt="" /></div>
      <div className="vs-card-name">{player.displayName}</div>
      <span className="vs-card-color">{player.color}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Game Screen
// ─────────────────────────────────────────────────────────────────
type Corner = 'tl' | 'tr' | 'bl' | 'br';
function GameScreen({
  state, board, myUserId, isMyTurn, eligibleTokens, lastRoll, turnDeadline,
  displayPos, moveMs, animating, animatingKey, svgRef, onRoll, onPickToken,
  autoRoll, onToggleAutoRoll, maxStrikes,
}: {
  state: EngineState;
  board: { snakes: SnakeData[]; ladders: LadderData[] };
  myUserId: string | null;
  isMyTurn: boolean;
  eligibleTokens: ('A' | 'B')[] | null;
  lastRoll: { value: number; byPlayerId: number } | null;
  turnDeadline: number | null;
  displayPos: Record<string, number>;
  moveMs: number;
  animating: boolean;
  animatingKey: string | null;
  svgRef: React.RefObject<SVGSVGElement>;
  onRoll: () => void;
  onPickToken: (letter: 'A' | 'B') => void;
  autoRoll: boolean;
  onToggleAutoRoll: () => void;
  maxStrikes: number;
}) {
  const current = state.players[state.currentPlayerIndex];

  // Place "me" bottom-left; others fill the remaining corners.
  const meIdx = Math.max(0, state.players.findIndex((p) => p.userId === myUserId));
  const ordered = [...state.players.slice(meIdx), ...state.players.slice(0, meIdx)];
  const cornerOrder: Corner[] = state.players.length <= 2 ? ['bl', 'tr'] : ['bl', 'tl', 'tr', 'br'];
  const panelMap: Partial<Record<Corner, EnginePlayer>> = {};
  ordered.forEach((p, i) => { if (cornerOrder[i]) panelMap[cornerOrder[i]] = p; });

  // Tokens on the board. A token's rendered square is its animation
  // override (displayPos) if one is active, else the server position.
  // Clicking is disabled while any move is animating.
  const tokens: TokenRender[] = useMemo(() => {
    // First pass: collect every on-board token + count how many share each
    // square (so the renderer can lay them out in a neat cluster instead of
    // the old runaway horizontal spread — important at the start where ALL
    // tokens sit on square 1).
    const raw: TokenRender[] = [];
    const perBlock = new Map<number, number>();
    for (const p of state.players) {
      const mine = p.userId === myUserId;
      for (const t of p.tokens) {
        const key = `${p.color}-${t.letter}`;
        const pos = displayPos[key] ?? t.position;
        if (pos === 0) continue;
        const clickable = !animating && mine && isMyTurn
          && state.awaiting === 'token_pick' && !!eligibleTokens?.includes(t.letter);
        raw.push({ ownerColor: p.color, letter: t.letter, position: pos, isHomed: t.isHomed, clickable, slotIndex: 0, slotCount: 1 });
        perBlock.set(pos, (perBlock.get(pos) || 0) + 1);
      }
    }
    // Second pass: assign each token its slot index within its square.
    const seen = new Map<number, number>();
    for (const r of raw) {
      const i = seen.get(r.position) || 0;
      r.slotIndex = i;
      r.slotCount = perBlock.get(r.position) || 1;
      seen.set(r.position, i + 1);
    }
    return raw;
  }, [state, myUserId, isMyTurn, eligibleTokens, displayPos, animating]);

  const renderPanel = (corner: Corner) => {
    const p = panelMap[corner];
    if (!p) return <div className="w-[48%]" />;
    const isTurn = current?.playerId === p.playerId;
    const isMe = p.userId === myUserId;
    const diceValue = (isTurn && lastRoll && lastRoll.byPlayerId === p.playerId) ? lastRoll.value : null;
    const canRoll = !animating && isMe && isTurn && state.awaiting === 'roll' && diceValue == null;
    return (
      <PlayerPanel
        player={p} isMe={isMe} isTurn={isTurn}
        position={corner === 'tl' ? 'top-left' : corner === 'tr' ? 'top-right' : corner === 'bl' ? 'bottom-left' : 'bottom-right'}
        diceValue={diceValue} canRoll={canRoll} turnSeq={state.turnId} onRoll={onRoll}
        autoRoll={autoRoll} onToggleAutoRoll={onToggleAutoRoll} maxStrikes={maxStrikes}
      />
    );
  };

  return (
    <div id="game-screen"
      className="flex flex-col w-full h-full flex-grow goldern_border rounded-none sm:rounded-b-3xl relative z-[10] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#1a7a3a] via-[#13602f] to-[#06180e]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="flex flex-col flex-grow justify-center w-full max-w-[430px] mx-auto gap-1 sm:gap-2">
        <div className="w-full px-2 flex justify-between z-20">
          <div className="w-[48%] flex flex-col">{renderPanel('tl')}</div>
          <div className="w-[48%] flex flex-col">{renderPanel('tr')}</div>
        </div>

        <div className="board-wrapper flex items-center justify-center z-10 my-0 w-full">
          <div className="board-rotation-wrapper slim_goldern_border" style={{ aspectRatio: '1 / 1', width: 'var(--max-board-size)', maxWidth: '100%', height: 'auto', padding: 0, borderRadius: 12, overflow: 'hidden', background: 'rgba(0,0,0,0.25)' }}>
            <SnakeLadderBoard snakes={board.snakes} ladders={board.ladders} tokens={tokens} moveMs={moveMs} svgRef={svgRef} animatingKey={animatingKey} onPickToken={onPickToken} />
          </div>
        </div>

        <div className="w-full px-2 flex justify-between z-20">
          <div className="w-[48%] flex flex-col">{renderPanel('bl')}</div>
          <div className="w-[48%] flex flex-col">{renderPanel('br')}</div>
        </div>
      </div>

      {/* Hint line — FIXED height + single line (no wrap) so it can never
          change size as the message toggles → zero board jitter, even on
          narrow mobile widths where text would otherwise wrap. */}
      <div
        className="text-center text-[12px] font-bold flex items-center justify-center"
        style={{ height: 26, lineHeight: '26px', whiteSpace: 'nowrap', overflow: 'hidden' }}
      >
        {animating
          ? <span className="text-white/70">Moving…</span>
          : (isMyTurn && state.awaiting === 'token_pick')
            ? <span className="text-[#dfff7a] animate-pulse">Tap a token to move</span>
            : null}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PlayerPanel
// ─────────────────────────────────────────────────────────────────
function PlayerPanel({
  player, isMe, isTurn, position, diceValue, canRoll, turnSeq, onRoll,
  autoRoll, onToggleAutoRoll, maxStrikes,
}: {
  player: EnginePlayer; isMe: boolean; isTurn: boolean;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  diceValue: number | null; canRoll: boolean; turnSeq: number; onRoll: () => void;
  autoRoll: boolean; onToggleAutoRoll: () => void; maxStrikes: number;
}) {
  const color = player.color;
  const isTop = position.startsWith('top');
  const isLeft = position.endsWith('left');
  const kicked = player.seatStatus === 'forfeited';
  const absent = !player.connected;
  const glowClass = isTurn && !absent && !kicked ? `glow-${color}` : '';
  const homed = player.tokens.filter((t) => t.isHomed).length;

  if (kicked) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className={`timer-container w-[95%] ${isTop ? 'order-last' : 'order-first'}`} style={{ visibility: 'visible', opacity: 0.3 }}><div className="timer-fill" style={{ width: '0%' }} /></div>
        <div className="player-profile slim_goldern_border player-profile--kicked">
          <div className="flex-1 text-center text-[11px] uppercase tracking-widest text-neutral-400 py-4">Left the match</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      {/* Timer TRACK is ALWAYS present (fixed height, always visible) so a
          turn switch never adds/removes a box → no board jitter. Only the
          fill animates for the active seat; the idle seat shows an empty
          (dimmed) track. */}
      <div className={`timer-container w-[95%] ${isTop ? 'order-last' : 'order-first'}`} style={{ visibility: 'visible', opacity: isTurn ? 1 : 0.3 }}>
        {isTurn ? <TimerFill key={`${color}-${turnSeq}`} /> : <div className="timer-fill" style={{ width: '0%' }} />}
      </div>
      <div className={['player-profile slim_goldern_border relative', glowClass, isMe && isTurn ? 'is-my-turn' : '', absent ? 'player-profile--absent' : ''].filter(Boolean).join(' ')}>
        {isLeft && isTurn && (
          <div className="absolute -right-6 top-1/2 animate-bounce-r z-50" style={{ color: COLOR_HEX[color], filter: `drop-shadow(0 0 5px ${COLOR_HEX[color]})` }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="21,9 10,9 10,4 2,12 10,20 10,15 21,15" /></svg>
          </div>
        )}
        {!isLeft && isTurn && (
          <div className="absolute -left-6 top-1/2 animate-bounce-l z-50" style={{ color: COLOR_HEX[color], filter: `drop-shadow(0 0 5px ${COLOR_HEX[color]})` }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="3,9 14,9 14,4 22,12 14,20 14,15 3,15" /></svg>
          </div>
        )}
        <div className="flex flex-col items-center w-[55px] shrink-0">
          <div className={`avatar-circle slim_goldern_border avatar-${color}`}>
            <img src={avatarFor(player)} alt="" />
          </div>
          <div className="strikes-container mt-1">
            {Array.from({ length: maxStrikes }, (_, i) => <div key={i} className={`strike-dot ${i < player.strikes ? 'active' : ''}`} />)}
          </div>
        </div>
        <div className="flex flex-col justify-center flex-grow pl-1 min-w-0 overflow-hidden z-10 min-h-[50px]">
          <span className="text-[11px] text-white font-bold leading-tight truncate uppercase w-full block">{player.displayName}</span>
          {isMe ? (
            <span className="mt-1 flex items-center gap-1 w-max leading-none">
              <span className="bg-white text-black px-1.5 py-0.5 rounded text-[8px] font-black animate-pulse">YOU</span>
            </span>
          ) : (
            <div className="flex items-center mt-1">
              <span className="text-[10px] text-emerald-300 font-bold truncate">{homed}/2 home</span>
            </div>
          )}
        </div>
        {/* Dice column reserves a fixed box (dice + AUTO pill) at all times so
            the dice/AUTO appearing only on the active turn never reflows the
            panel. The AUTO pill (auto-roll toggle) shows only on MY turn —
            same style + behaviour as Ludo. */}
        <div className="flex flex-col items-center justify-center ml-auto shrink-0 gap-1" style={{ width: 44, height: 66 }}>
          {isTurn && (
            <div className="dice-slot relative">
              <DiceButton color={color} value={diceValue} waiting={canRoll && diceValue == null} disabled={!canRoll} onClick={onRoll} />
            </div>
          )}
          {isMe && isTurn && (
            <button
              type="button"
              onClick={onToggleAutoRoll}
              aria-pressed={autoRoll}
              aria-label={`Auto-roll ${autoRoll ? 'on' : 'off'}`}
              title={`Auto-roll — ${autoRoll ? 'ON' : 'OFF'} (tap to toggle)`}
              className={[
                'w-[44px] text-[9px] font-black tracking-[0.1em] leading-none py-[3px] rounded-full border transition-colors select-none text-center',
                autoRoll
                  ? 'bg-[#67e67a] text-black border-[#2e8b3b] shadow-[0_0_6px_rgba(103,230,122,0.55)]'
                  : 'bg-[#0c2a16] text-[#9ab0a0] border-[#3a6b48] hover:bg-[#123a1f]',
              ].join(' ')}
            >
              AUTO
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TimerFill() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.width = '100%'; el.style.backgroundColor = '#ffffff'; el.style.transition = 'none';
    void el.offsetWidth;
    el.style.transition = `width ${TURN_SECONDS}s linear, background-color ${TURN_SECONDS}s linear`;
    el.style.width = '0%'; el.style.backgroundColor = '#ef447a';
  }, []);
  return <div ref={ref} className="timer-fill" />;
}

function DiceButton({ color, value, waiting, disabled, onClick }: {
  color: Color; value: number | null; waiting: boolean; disabled: boolean; onClick: () => void;
}) {
  const [tumbling, setTumbling] = useState(false);
  const prevValue = useRef<number | null>(null);
  useEffect(() => {
    if (prevValue.current == null && value != null) {
      setTumbling(true);
      const t = setTimeout(() => setTumbling(false), 400);
      prevValue.current = value;
      return () => clearTimeout(t);
    }
    if (value == null) setTumbling(false);
    prevValue.current = value;
  }, [value]);
  const classes = ['dice-btn', `dice-${color}`, waiting ? 'waiting-to-roll' : '', tumbling ? 'tumble' : '',
    !tumbling && value != null ? `face-${value}` : '', 'absolute inset-0 w-full h-full border-none rounded-md'].filter(Boolean).join(' ');
  return (
    <button className={classes} onClick={onClick} disabled={disabled}>
      <span className="roll-text">ROLL</span>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <div key={n} className={`dot dot-${n}`} />)}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Result + Quit
// ─────────────────────────────────────────────────────────────────
function ResultScreen({ kind, amount, winnerName, platformFee, onHome, onNewMatch }: {
  kind: 'win' | 'loss' | 'kick'; amount: number; winnerName?: string; platformFee?: number;
  onHome: () => void; onNewMatch: () => void;
}) {
  const icon = kind === 'win' ? IMG.victory : kind === 'kick' ? IMG.kicked : IMG.defeat;
  const stateClass = kind === 'win' ? 'win-state' : kind === 'loss' ? 'lose-state' : 'kicked-state';
  const title = kind === 'win' ? 'YOU WON!' : kind === 'kick' ? 'KICKED OUT' : 'YOU LOST';
  const sub = kind === 'win' ? 'Glory is yours'
    : kind === 'loss' ? (winnerName ? `${winnerName} won the pot` : 'Better luck next time')
    : 'You were removed from the match';
  const amountLabel = kind === 'win' ? 'You Won' : kind === 'loss' ? 'Pot Claimed' : 'You Lost';
  return (
    <div id="result-screen" className="overlay-screen">
      {kind === 'win' && <CoinRain />}
      <div className={`result-card ${stateClass}`}>
        <div className="result-rays" />
        <div className="result-icon-wrap"><img src={icon} alt="" /></div>
        <h1 className="result-title">{title}</h1>
        <p className="result-subtitle">{sub}</p>
        <div className="result-amount-box">
          <span className="result-amount-label">{amountLabel}</span>
          <span className="result-amount-value">
            <img src={IMG.coin} className="w-6 h-6 object-contain drop-shadow" alt="" />
            <span>{fmt(amount)}</span>
          </span>
        </div>
        {kind === 'win' && platformFee && platformFee > 0 ? (
          <div className="result-amount-fee">Platform fee: {fmt(platformFee)}</div>
        ) : null}
        <div className="result-btn-row">
          <button className="result-btn result-btn-secondary" onClick={onHome}>Home</button>
          <button className="result-btn result-btn-primary" onClick={onNewMatch}>New Match</button>
        </div>
      </div>
    </div>
  );
}

function QuitConfirm({ amount, onStay, onQuit }: { amount: number; onStay: () => void; onQuit: () => void }) {
  return (
    <div id="quit-confirm-modal" className="quit-backdrop">
      <div className="quit-card">
        <div className="quit-title">Forfeit the match?</div>
        <div className="quit-warning">
          Leaving now will instantly <b>end the game for you</b> and your bet will be <b>forfeited</b>.<br />
          You will lose <span className="quit-amount">{fmt(amount)}</span>
        </div>
        <div className="result-btn-row">
          <button className="result-btn result-btn-secondary" onClick={onStay}>Stay</button>
          <button className="result-btn result-btn-danger" onClick={onQuit}>Quit</button>
        </div>
      </div>
    </div>
  );
}

function CoinRain() {
  const [coins, setCoins] = useState<Array<{ key: number; left: number; size: number; duration: number; delay: number }>>([]);
  useEffect(() => {
    let nextKey = 0;
    const spawn = () => {
      const batch = Array.from({ length: 6 }).map(() => ({
        key: nextKey++, left: Math.random() * 100, size: 10 + Math.random() * 10, duration: 2 + Math.random() * 2.2, delay: Math.random() * 0.5,
      }));
      setCoins((prev) => [...prev, ...batch]);
      const expireKeys = new Set(batch.map((b) => b.key));
      setTimeout(() => setCoins((prev) => prev.filter((c) => !expireKeys.has(c.key))), 4500);
    };
    spawn();
    const interval = setInterval(spawn, 350);
    return () => clearInterval(interval);
  }, []);
  return (
    <div id="coin-rain" className="coin-rain">
      {coins.map((c) => (
        <span key={c.key} className="coin" style={{ left: `${c.left}%`, width: c.size, height: c.size, animationDuration: `${c.duration}s`, animationDelay: `${c.delay}s` }} />
      ))}
    </div>
  );
}
